/**
 * The charging cable strung between two cars, the stepping stones over it, and
 * the tolppa it is plugged into.
 *
 * The cable is what cuts the power when it is walked into, so it is hashed;
 * what happens to the lights afterwards is not.
 */

import { solid, type CarBadge, type MaterialKey, type SolidSpec } from '../world/Level';
import { CAR, camper, plateFor } from './levelParkkipaikka';

/* ------------------------------------------------------------ ansalanka - */

/**
 * The lot was full by the time these four arrived, so they left the cars in a
 * row across the yard where the pihatie opens into it - right over the drive,
 * which is exactly the sort of thing that happens. The row is a wall straight
 * across the fast line with exactly one hole in it.
 *
 * That hole is not on the ground. The row is shut end to end - the bemari sits
 * flank to flank with the audi beside it - and the way through is *over the
 * audi*, off the harkot dumped in front of its nose. Four hops: three pallets
 * at `stepHeight`, and then the audi's own roof at `audiHeight`. None of them
 * is free and none of them is reachable cold, which is the whole obstacle: the
 * row is a wall to a runner who arrives slow and a staircase to one who does
 * not.
 *
 * The numbers are the point, so they are written down here. The apex is
 * `jumpApex` and it runs 0.713 m cold to 0.933 m on a full meter; the line
 * arrives here with about 1.26 on it, which buys 0.773 m. So:
 *
 *   ground -> lava 1   0.73 m needs a meter of 1.20. Cold you cannot.
 *   lava -> lava       flat, twice, and each hop takes `jumpFactor` off the
 *                      meter: 1.26 -> 1.08 -> `MOMENTUM.min` and it stays
 *                      there, because `breakMomentum` floors at the min.
 *   lava 3 -> katto    taken on an empty meter, so the apex is the cold
 *                      0.713 m and the roof has to sit under 1.443 m.
 *
 * `audiHeight` is 1.35 m against that 1.443, which leaves 9 cm - and it is why
 * the audi is the one car on the route that is not `CAR.bodyHeight`. Every
 * other roof in the game is deliberately out of reach of any apex the meter can
 * buy; this one is in reach of exactly one launchpad, and the launchpad is
 * three pallets of concrete nobody can move.
 *
 * WHY THREE PALLETS AND NOT ONE. A single stack was a gate and nothing else:
 * arrive with 1.20 on the meter and the rest of the crossing came free, so the
 * whole set-piece was decided a second and a half before you got to it. Three
 * of them make it a run rather than a threshold - `stepSize` of top to land on,
 * `stepPitch` between one and the next, and the meter falling a step at a time
 * while you do it. What the chain asks for is spelled out in `stepGap`: by the
 * last pallet there is no meter left to spend, so the arc onto the roof is the
 * shortest one the game has, and it has to be taken off the *far* edge.
 *
 * The way round is on the left, past the bemari - which is a sähköauto, and is
 * on charge from the yard's tolppa. So the way round has a cable lying across
 * it at shin height, and that cable is the ansalanka the thing is named after.
 * It hangs at shin height rather than lying flat, so the left line is *safe*
 * only if you actually jump - and a jump costs vauhti.
 *
 * WHERE THE CROSSING SITS is settled by the line out the far side of it. The
 * klapiteline stands on the palju's north rim, so the run leaves this row on
 * its right-hand side and holds that side the whole way to the tub. The audi
 * is on that line and has not moved; what closed the gap is the *bemari*
 * sliding a slot's width right to meet it, and its charging cable stretching
 * by the same 1.25 m to follow.
 *
 * That direction is not arbitrary. Closing the gap from the other side would
 * have dragged the crossing left, towards the cable - and the cable is the
 * alternative line, so pulling the two together is exactly backwards. Shutting
 * it from the left leaves the crossing where the run already wanted to be and
 * puts the cable four metres off it, which is what makes going round the left
 * a decision rather than a wobble. Both ends of the row stay anchored where
 * they were: nothing on the right of the audi moved, and neither did the
 * tolppa or the car parked behind it.
 *
 * There is no third way. The row is four vehicles and a wheelbarrow long and
 * it is anchored at both ends: the wheelbarrow runs into the kuusikko along
 * the yard's right-hand edge (see `SPRUCE`), and the car behind the tolppa
 * runs into the boards down the left-hand one (see `AITAUS`). The gap and the
 * cable are the two lines there are.
 */
export const ANSALANKA = {
  /**
   * Where the bemari's tail sits. The rest of the row is staggered off it, and
   * the whole row hangs off this one number.
   *
   * It came 2.0 m further from the lot when the launchpad became three pallets,
   * and the reason is the vauhti meter rather than the parking. The first
   * pallet is the gate and the gate is priced at `CAR.bootHeight`, which the
   * run cannot clear until the meter is around 1.21 - and coming off
   * PARKING_END the meter is at 1.10 and climbing at `MOMENTUM.buildPerSecond`,
   * so where the gate can stand is decided by how much open yard it has behind
   * it. A chain of three needs 5.9 m of yard in front of the audi's nose;
   * moving the nose is what bought them without moving the gate up the piha
   * into ground the meter has not paid for yet.
   */
  x: 107.6,
  /**
   * The audi's left flank. This is the anchor the row is built from now: it is
   * on the line the run holds from the lot to the klapiteline, and everything
   * that closed the gap moved towards it rather than it moving.
   */
  audiZ: -17.325,
  /**
   * The audi's roofline, and the one place in the level a car is not
   * `CAR.bodyHeight`. See the top of this block for the arithmetic; the short
   * version is that this is the number that decides whether the row has a way
   * through it at all, so it is authored against the apex and not against what
   * an audi is.
   */
  audiHeight: 1.35,
  /**
   * The harkot in front of the audi's nose: the launchpad, and the only steps
   * on the route between the ground and a roof.
   *
   * `stepHeight` is the gate, and it is set by two numbers pulling opposite
   * ways. It has to sit over the 0.713 m cold apex or the row is not a gate at
   * all; it has to sit far enough *under* the 0.773 m the line actually
   * arrives with to leave somewhere to land. It is the lot's own lid height
   * rather than a figure of its own, because that is the height this game has
   * already priced as "a wall at a walk and a hurdle at thirty" - and the
   * three pallets are all of it, so the gate is the first one and the other
   * two are flat.
   *
   * They are all the same height on purpose. A rising staircase would be the
   * obvious way to make the chain harder and it is the one thing this corner
   * of the level may not have: the bemari stands flank to flank with the audi
   * at `CAR.bodyHeight`, and it is out of reach by 57 mm measured from
   * `stepHeight` at a full meter. Raise the last pallet and the row grows a
   * lane along the top of it, which is the third way the whole set-piece is
   * written not to have.
   */
  stepHeight: CAR.bootHeight,
  /**
   * One pallet of blocks, square in plan.
   *
   * This is the target, and it is the difficulty. A hop off a pallet covers
   * about 2.2 m of ground, so at `stepPitch` you land a third of the way onto
   * the next one and have the rest of it to take off from - which sounds
   * generous until it is the third one in a row and the meter is empty. The
   * player's own radius makes the landing window `stepSize` plus 0.68 m; miss
   * it and you are on the grass in front of a shut row with nothing left.
   */
  stepSize: 1.4,
  /** Three. Any more and the meter is on the floor before the chain is. */
  stepCount: 3,
  /**
   * Centre to centre along the run, and the gap between two pallets rather
   * than the length of the hop over it: the hop is `stepPitch` less the
   * `stepSize` you spend standing on either end of it.
   *
   * Under a jump's own 2.2 m of ground on purpose. Set it *at* the arc and
   * every landing is on the pallet's near lip, which is the same trick three
   * times and no decision; set it here and the arc overshoots the lip, so
   * where you land is decided by where you took off. That is what makes three
   * hops three hops rather than one held space bar.
   */
  stepPitch: 2.25,
  /**
   * How far towards the lot each pallet stands of the one after it, across
   * the run rather than along it.
   *
   * The chain is laid on a slant because the *run* is on a slant. The lot
   * empties into the piha well north of the audi and the crossing has to end
   * up on the audi's own centre line, so there is a diagonal to be spent
   * somewhere; laid like this the pallets are what spends it. That is the
   * second half of what the chain asks for, and the half that cannot be
   * learned as a rhythm: a jump taken across the line lands across the line,
   * and there are three of them to get wrong instead of one.
   *
   * It used to be spent on the ground, on a mark in front of the stack, and
   * the note there said in as many words that the take-off had to be square.
   * It does not any more - the take-off is square and the *flight* is not.
   */
  stepDrift: 0.5,
  /**
   * How far the last pallet stands off the audi's nose, and the number that
   * took the longest to find.
   *
   * Stacked against the car it does not work at all: a jump turns speed into
   * height over about a metre of ground, and a player pressed against a car's
   * face has none of that metre left. The blocks have to stand back far
   * enough that there is *top* to run along before the last take-off, or the
   * whole line is a man climbing onto a box and hitting a wing.
   *
   * It came in from 1.4 m when the chain grew to three, and the reason is the
   * meter. One hop used to leave 1.08 on it and the arc onto the roof was a
   * long one; three leave `MOMENTUM.min`, which is the shortest arc in the
   * game. Over 1.4 m of stand-off that arc arrives at the audi's nose about a
   * centimetre above the roofline - a gate that opens and shuts on rounding.
   * At 1.1 it arrives with room, and the thing that decides the hop is where
   * on the pallet you left from, which is what it should be.
   */
  stepGap: 1.5,
  /**
   * The cable is strung between the tolppa's socket and the bemari's flap, so
   * it hangs *off the ground* rather than lying on it - which is what a charging
   * cable across a yard actually does, and what makes this a snare rather than
   * a threshold strip.
   *
   * Feet have to clear `cableY + cableHeight` = 0.44 m. The jump apex is
   * 0.71 m and the player is above 0.44 m for 0.31 s of it, so there is a real
   * window to hit and a real way to miss it. Collision has no step-up, so there
   * is no walking over it either: you hop it or you stop.
   */
  cableY: 0.22,
  cableHeight: 0.22,
  /**
   * Half-width of the cable's collider. Wider than the wire itself on purpose:
   * the drawn cable meanders, and the box has to contain every millimetre of
   * where it wanders to. A visual that leaves its collider is a lie in a game
   * where you thread gaps at 30 km/h.
   */
  cableHalfWidth: 0.19,
  /**
   * How far out from the bemari's flank the tolppa stands.
   *
   * It grew by the slot's old 1.25 m when the bemari slid right to shut the
   * gap: the post is bolted to the ground and did not move, so the slack went
   * into the cable. A longer lead across the same yard is what actually
   * happens when somebody moves the car and not the charger.
   */
  cableLength: 5.45,
} as const;

/** The line the run holds through the row: over the middle of the audi. */
export const ANSALANKA_LINE_Z = ANSALANKA.audiZ + CAR.width / 2;
/** The bemari's outer flank. Everything left of this belongs to the cable. */
export const BEMARI_Z = ANSALANKA.audiZ - CAR.width;
export const TOLPPA_Z = BEMARI_Z - ANSALANKA.cableLength;
/** The post's back face. Whatever is parked behind it is measured off this. */
export const TOLPPA_BACK_Z = TOLPPA_Z - 0.4;
/** Midway down the flank: too far from either end of the car to be dodged. */
export const CABLE_X = ANSALANKA.x + CAR.length / 2;

/** A car parked broadside to the run - lying along x, not along z like a bay. */
function rowCar(
  name: string,
  material: MaterialKey,
  minX: number,
  minZ: number,
  plate?: string,
  badge?: CarBadge,
  height: number = CAR.bodyHeight,
): SolidSpec {
  return {
    ...solid(name, material, minX, minZ, minX + CAR.length, minZ + CAR.width, height),
    shape: 'car',
    // A hand-written erikoistunnus if this one was given one, and otherwise
    // whatever the yard hands it. See `plateFor`.
    plate: plate ?? plateFor(name),
    badge,
  };
}

/**
 * Left to right. Nobody parking on an open yard lines up with anybody, so the
 * noses are staggered - which also means the gap funnels rather than starting
 * flat.
 */
export const ANSALANKA_ROW = [
  // The two cars anybody looks at twice, because they are the two the gap runs
  // between - so they are the two whose plates are worth reading. Every car in
  // the level wears one now (see `plateFor`); these two wear an erikoistunnus
  // instead of the yard's ABC-123, because a personal plate is exactly what is
  // on the car parked across a driveway and charging off somebody else's
  // tolppa, and exactly what is on the audi parked beside it.
  rowCar('ansalanka-bemari', 'carEv', ANSALANKA.x, BEMARI_Z, 'WINKZI', 'bmw'),
  // The audi is the way through, so it is the one car here drawn to the height
  // a car actually is rather than to the height a hurdle has to be.
  rowCar(
    'ansalanka-auto-2',
    'carAlt',
    ANSALANKA.x - 0.7,
    ANSALANKA.audiZ,
    'PROFFA',
    'audi',
    ANSALANKA.audiHeight,
  ),
  // Behind the tolppa, at the left-hand end of the row: 30 cm off the post and
  // 7.5 cm off the boards, both far under the 0.68 m player, so the row has one
  // hole in it and not two. It is a whole car rather than something smaller
  // because it has to reach past the cable's own x on both sides - anything
  // short here leaves a lane that goes *under* the south end of the cable
  // instead of over it, which is the third way this row is not allowed to have.
  rowCar('ansalanka-auto-3', 'car', ANSALANKA.x + 0.3, TOLPPA_BACK_Z - 0.3 - CAR.width),
];

/** "Voit tarvittaessa vuokrata matkailuautopaikan pihapiiristä." Somebody did. */
export const ANSALANKA_CAMPER = camper(
  'ansalanka-matkailuauto',
  ANSALANKA.x - 1.4,
  ANSALANKA.audiZ + CAR.width + 0.3,
);

/**
 * The harkot in front of the audi's nose: three pallets of concrete block, two
 * courses to a pallet, left over from whatever the pihapiiri last had poured.
 * The lorry came up the drive, the kuormain reached off one side of it, and
 * they went down in the order they came off - which is a slant across the yard
 * and not a row square to anything, because nobody lines up a pallet.
 *
 * They are the launchpad, and they are also the reason the row can be shut.
 * With the gap gone the row needs a way over it or it stops being an obstacle
 * and starts being a wall, and a wall across the only line is where a route
 * ends rather than where it gets interesting.
 *
 * They stand *in front of* the row rather than in it. A stack standing in the
 * row would be a hole with a hurdle in it - hop up, hop down, carry on, and
 * the audi behind it never enters into it. In front of the nose they are a
 * flight of steps that only goes one place: up onto the car.
 *
 * They are numbered along the run, so `ansalanka-harkot-1` is the one the
 * grass hands you and `-3` is the one the roof is taken off. See `ANSALANKA`
 * for `stepPitch`, `stepDrift` and `stepGap`, which are the three numbers the
 * chain actually is.
 */
export const ANSALANKA_HARKOT: SolidSpec[] = Array.from(
  { length: ANSALANKA.stepCount },
  (_, index): SolidSpec => {
    // Counted back from the last pallet, because the last one is the only one
    // whose place is decided by something else: it stands `stepGap` off the
    // audi's nose, and everything before it is spaced off it in turn.
    const back = ANSALANKA.stepCount - 1 - index;
    const centreX = ANSALANKA.x - 0.7 - ANSALANKA.stepGap - ANSALANKA.stepSize / 2 - back * ANSALANKA.stepPitch;
    const centreZ = ANSALANKA_LINE_Z + back * ANSALANKA.stepDrift;
    const half = ANSALANKA.stepSize / 2;
    return solid(
      `ansalanka-harkot-${index + 1}`,
      'stone',
      centreX - half,
      centreZ - half,
      centreX + half,
      centreZ + half,
      ANSALANKA.stepHeight,
    );
  },
);
